/**
 * Utility functions for managing share tokens in localStorage
 */

const SHARE_TOKEN_KEY = 'kip_share_ref';
const SHARE_TOKEN_TIMESTAMP_KEY = 'kip_share_ref_timestamp';
const SHARE_CONTENT_TYPE_KEY = 'kip_share_content_type';
const SHARE_CONTENT_ID_KEY = 'kip_share_content_id';
const TOKEN_EXPIRY_DAYS = 30;

/**
 * Store a share token in localStorage with timestamp
 * Overwrites any previously stored token
 * @param {string} token - The share token to store
 */
export function storeShareToken(token) {
  if (!token) return;

  const timestamp = Date.now();
  localStorage.setItem(SHARE_TOKEN_KEY, token);
  localStorage.setItem(SHARE_TOKEN_TIMESTAMP_KEY, timestamp.toString());
}

/**
 * Get the stored share token if it exists and hasn't expired
 * @returns {string|null} The share token or null if not found/expired
 */
export function getShareToken() {
  const token = localStorage.getItem(SHARE_TOKEN_KEY);
  const timestampStr = localStorage.getItem(SHARE_TOKEN_TIMESTAMP_KEY);

  if (!token || !timestampStr) {
    return null;
  }

  // Check if token has expired (30 days)
  const timestamp = parseInt(timestampStr, 10);
  const now = Date.now();
  const daysSinceStored = (now - timestamp) / (1000 * 60 * 60 * 24);

  if (daysSinceStored > TOKEN_EXPIRY_DAYS) {
    // Token expired, clean it up
    clearShareToken();
    return null;
  }

  return token;
}

/**
 * Clear the stored share token
 */
export function clearShareToken() {
  localStorage.removeItem(SHARE_TOKEN_KEY);
  localStorage.removeItem(SHARE_TOKEN_TIMESTAMP_KEY);
  clearShareContent();
}

/**
 * Store content type and ID along with the share token
 * @param {string} contentType - 'set' or 'track'
 * @param {number|string} contentId - ID of the content
 */
export function storeShareContent(contentType, contentId) {
  if (contentType && contentId) {
    localStorage.setItem(SHARE_CONTENT_TYPE_KEY, contentType);
    localStorage.setItem(SHARE_CONTENT_ID_KEY, String(contentId));
  }
}

/**
 * Get the stored share content info
 * @returns {{contentType: string, contentId: string}|null} Content info or null if not found
 */
export function getShareContent() {
  const contentType = localStorage.getItem(SHARE_CONTENT_TYPE_KEY);
  const contentId = localStorage.getItem(SHARE_CONTENT_ID_KEY);

  if (contentType && contentId) {
    return {
      contentType,
      contentId: parseInt(contentId, 10),
    };
  }

  return null;
}

/**
 * Clear the stored share content info
 */
export function clearShareContent() {
  localStorage.removeItem(SHARE_CONTENT_TYPE_KEY);
  localStorage.removeItem(SHARE_CONTENT_ID_KEY);
}

/**
 * Extract ref parameter from URL and store it
 * Also extracts and stores content type and ID from the URL path
 * Should be called when app loads or when navigating to a page with ref parameter
 * @param {string} url - The URL to check (defaults to window.location.href)
 */
export function extractAndStoreShareToken(url = null) {
  const urlToCheck = url || window.location.href;
  const urlObj = new URL(urlToCheck);
  const refParam = urlObj.searchParams.get('ref');

  if (refParam) {
    storeShareToken(refParam);

    // Extract content type and ID from URL path
    const pathname = urlObj.pathname;
    // Match patterns like /sets/546 or /tracks/123
    const setMatch = pathname.match(/^\/sets\/(\d+)/);
    const trackMatch = pathname.match(/^\/tracks\/(\d+)/);

    if (setMatch) {
      storeShareContent('set', parseInt(setMatch[1], 10));
    } else if (trackMatch) {
      storeShareContent('track', parseInt(trackMatch[1], 10));
    }

    // Remove ref from URL to clean it up (but preserve other params)
    urlObj.searchParams.delete('ref');
    const newUrl =
      urlObj.pathname +
      (urlObj.search ? urlObj.search : '') +
      (urlObj.hash || '');
    // Update URL without page reload
    window.history.replaceState({}, '', newUrl);
  }
}
